import { useEffect, useRef, useState } from 'react';

// Hook for detecting when element enters viewport
export function useInView(options = {}) {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        // Once in view, stop observing (animation plays once)
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      }
    }, {
      threshold: options.threshold || 0.1,
      rootMargin: options.rootMargin || '0px 0px -50px 0px',
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [options.threshold, options.rootMargin]);

  return [ref, isInView];
}

// Animated section wrapper component
export function AnimatedSection({ children, className = '', delay = 0, animation = 'fadeUp' }) {
  const [ref, isInView] = useInView();
  
  const animations = {
    fadeUp: 'translate-y-8 opacity-0',
    fadeDown: '-translate-y-8 opacity-0',
    fadeLeft: 'translate-x-8 opacity-0',
    fadeRight: '-translate-x-8 opacity-0',
    fade: 'opacity-0',
    scale: 'scale-95 opacity-0',
  };

  const baseStyles = animations[animation] || animations.fadeUp;

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        transitionDelay: `${delay}ms`,
        transform: isInView ? 'none' : undefined,
        opacity: isInView ? 1 : undefined,
      }}
      data-animate={!isInView ? baseStyles : ''}
    >
      <div
        className={`transition-all duration-700 ease-out ${!isInView ? baseStyles : ''}`}
        style={{ transitionDelay: `${delay}ms` }}
      >
        {children}
      </div>
    </div>
  );
}

// Staggered children animation wrapper
export function StaggeredList({ children, className = '', staggerDelay = 100 }) {
  const [ref, isInView] = useInView();
  
  return (
    <div ref={ref} className={className}>
      {Array.isArray(children) ? children.map((child, index) => (
        <div
          key={index}
          className={`transition-all duration-500 ease-out ${!isInView ? 'translate-y-6 opacity-0' : ''}`}
          style={{ transitionDelay: isInView ? `${index * staggerDelay}ms` : '0ms' }}
        >
          {child}
        </div>
      )) : children}
    </div>
  );
}
